import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { useAppStore } from '@/store/loadingStore';
import { useNotificationStore } from '@/store/notificationStore';
import { useConnectionStore } from '@/store/connectionStore';
import { useConnectionActions } from '@/connections/hooks/useConnectionActions';
import type { Device } from '@packages/types/device/dto';
import type { LogRequestParams } from '@/components/dialogs/LogRequestDialog';
import * as liteLogApi from '@/api/device/logs/liteLog';
import * as miniLogApi from '@/api/device/logs/miniLog';
import { parseDatalogApiError } from '@/utils/errors';
import { getFileTypeName } from '@/utils/datalogFileType';

interface ExtendedLogRequestParams extends LogRequestParams {
  isBatchMode?: boolean;
}

interface UseDatalogLogRequestParams {
  devices: Device[];
}

export function useDatalogLogRequest({ devices }: UseDatalogLogRequestParams) {
  const { t } = useTranslation('datalog');
  const { showLoading, hideLoading } = useAppStore();
  const { showSuccess, showError, showWarning } = useNotificationStore();
  const setDownloading = useConnectionStore((state: any) => state.setDownloading);
  const { downloadDatalog } = useConnectionActions();
  const connectionStore = useConnectionStore();
  const { protocol, device: connectedDevice } = connectionStore;

  const isCradleConnected = protocol.type === 'serial' && connectedDevice.type !== 'none';
  const isBleConnected = protocol.type === 'ble' && connectedDevice.type !== 'none';
  const connectedSerialNumber =
    connectedDevice.type !== 'none' ? connectedDevice.activeDeviceId : null;

  const [logRequestDialog, setLogRequestDialog] = useState<{
    isOpen: boolean;
    device: Device | null;
  }>({ isOpen: false, device: null });

  const openLogRequestDialog = useCallback(() => {
    if (!connectedSerialNumber) {
      showError(i18nKeys.device.notConnected, i18nKeys.device.notConnected);
      return;
    }

    const normalizedConnected = connectedSerialNumber.trim().toUpperCase();
    const connectedDeviceItem = devices.find(d => {
      const normalizedDevice = (d.serial_number || '').trim().toUpperCase();
      return normalizedDevice === normalizedConnected;
    });

    if (!connectedDeviceItem) {
      showError(i18nKeys.datalog.deviceNotInSite, i18nKeys.datalog.deviceNotInSite);
      return;
    }

    setLogRequestDialog({ isOpen: true, device: connectedDeviceItem });
  }, [connectedSerialNumber, devices, showError]);

  const closeLogRequestDialog = useCallback(() => {
    setLogRequestDialog({ isOpen: false, device: null });
  }, []);

  const handleLogRequest = useCallback(
    async (device: Device, params: ExtendedLogRequestParams, existingLogId?: string) => {
      try {
        const result = await downloadDatalog(
          {
            serialNumber: device.serial_number,
            deviceType: (device.device_type === 'bellmini' ? 'bellmini' : 'belllite') as
              | 'bellmini'
              | 'belllite',
            connectionType: params.connectionType,
            fileType: params.file_type,
            fileTypes: params.file_types,
            daysOfGasReading: params.days_of_gas_reading,
            transferMode: params.transfer_mode,
            isBatchMode: params.isBatchMode,
          },
          existingLogId
        );

        if (!result.success) {
          throw new Error(result.error || '로그 다운로드 실패');
        }

        return result;
      } catch (error: unknown) {
        console.error('❌ [handleLogRequest] 에러:', error);
        const e = parseDatalogApiError(error);
        if (e.type === 'warning') {
          showWarning(e.title, e.message);
        } else {
          showError(e.title, e.message);
        }
        throw error;
      }
    },
    [downloadDatalog, showError, showWarning]
  );

  const handleLogRequestConfirm = useCallback(
    async (params: LogRequestParams) => {
      if (!logRequestDialog.device) return;

      const targetDevice = logRequestDialog.device;
      const isMini = targetDevice.device_type === 'bellmini';

      if (params.file_types && params.file_types.length > 0) {
        let logRequests: Array<{
          logInfo: { log_id: string };
          fileType: number;
          typeName: string;
          optimizedDays: number | undefined;
          originalDays: number | undefined;
          isOptimized: boolean;
        }> = [];

        try {
          setDownloading(true);
          showLoading(
            t(i18nKeys.datalog.preparingDatalogRequest, { count: params.file_types.length }) +
              `\n${params.file_types.length}개 로그 타입을 순차적으로 다운로드합니다...`
          );

          const logRequestPromises = params.file_types.map(async currentFileType => {
            const typeName = getFileTypeName(currentFileType);
            const optimizedDays = params.days_of_gas_reading;
            const originalDays = params.days_of_gas_reading;
            const isOptimized = false;

            const logInfo = isMini
              ? await miniLogApi.createLogRequest({
                  serial_number: targetDevice.serial_number,
                  file_type: currentFileType,
                  transfer_mode: params.transfer_mode,
                  connection_type: 'bluetooth',
                })
              : await liteLogApi.createLogRequest({
                  serial_number: targetDevice.serial_number,
                  file_type: currentFileType,
                  transfer_mode: params.transfer_mode,
                  connection_type: params.connectionType === 'bluetooth' ? 'bluetooth' : 'cradle',
                  days_requested: originalDays,
                  days_optimized: optimizedDays,
                });

            return {
              logInfo,
              fileType: currentFileType,
              typeName,
              optimizedDays,
              originalDays,
              isOptimized,
            };
          });

          logRequests = await Promise.all(logRequestPromises);

          const results: Array<{
            success: boolean;
            fileType: number;
            typeName: string;
            error?: any;
          }> = [];
          let isCancelled = false;

          for (let i = 0; i < logRequests.length; i++) {
            const { logInfo, fileType, typeName, optimizedDays } = logRequests[i];
            const currentFileType = fileType;
            const overallProgressStart = Math.floor((i / logRequests.length) * 100);
            const overallProgressEnd = Math.floor(((i + 1) / logRequests.length) * 100);

            showLoading(
              t(i18nKeys.datalog.downloadingLogType, {
                current: i + 1,
                total: logRequests.length,
                typeName,
              }) + `\n ${overallProgressStart}%`
            );

            try {
              await handleLogRequest(
                targetDevice,
                {
                  ...params,
                  file_type: currentFileType,
                  file_types: undefined,
                  days_of_gas_reading: optimizedDays || params.days_of_gas_reading,
                  isBatchMode: true,
                },
                logInfo.log_id
              );

              results.push({ success: true, fileType: currentFileType, typeName });

              if (i < logRequests.length - 1) {
                showLoading(
                  t(i18nKeys.datalog.logTypeComplete, {
                    current: i + 1,
                    total: logRequests.length,
                  }) + `\n${overallProgressEnd}%`
                );
                await new Promise(resolve => setTimeout(resolve, 500));
              }
            } catch (error) {
              console.error(`❌ [${typeName}] 다운로드 실패:`, error);

              const errorMessage = error instanceof Error ? error.message : String(error);
              const isCancelError =
                errorMessage.includes('cancelled') ||
                errorMessage.includes('cancel') ||
                errorMessage.includes('취소');

              results.push({ success: false, fileType: currentFileType, typeName, error });

              try {
                const logApi = isMini ? miniLogApi : liteLogApi;
                await logApi.failLog(logInfo.log_id, errorMessage);
              } catch (failError) {
                console.error(`❌ 실패 처리 중 오류:`, failError);
              }

              if (isCancelError) {
                isCancelled = true;
                for (let j = i + 1; j < logRequests.length; j++) {
                  const remainingRequest = logRequests[j];
                  results.push({
                    success: false,
                    fileType: remainingRequest.fileType,
                    typeName: remainingRequest.typeName,
                    error: new Error('User cancelled download'),
                  });
                  try {
                    const logApi = isMini ? miniLogApi : liteLogApi;
                    await logApi.failLog(
                      remainingRequest.logInfo.log_id,
                      'User cancelled download'
                    );
                  } catch (failError) {
                    console.error(`❌ 취소 처리 중 오류:`, failError);
                  }
                }
                break;
              }
            }
          }

          const failed = results.filter(r => !r.success).length;

          if (isCancelled) {
            showError(
              i18nKeys.datalog.batchDownloadCancelled,
              i18nKeys.datalog.batchDownloadCancelled
            );
          } else if (failed > 0) {
            showError(i18nKeys.datalog.logRequestFailed, i18nKeys.datalog.batchSomeDownloadsFailed);
          } else {
            showSuccess(i18nKeys.datalog.logDownloadComplete, i18nKeys.datalog.logDownloadComplete);
          }
        } catch (error: unknown) {
          console.error('❌ [전체 로그 다운로드] 치명적 오류:', error);
          // 치명적 오류 시 생성된 모든 로그를 failed로 처리
          const logApi = isMini ? miniLogApi : liteLogApi;
          for (const req of logRequests) {
            try {
              await logApi.failLog(req.logInfo.log_id, 'Batch download aborted');
            } catch {
              // 이미 실패/완료된 로그는 무시
            }
          }
          const e = parseDatalogApiError(error);
          if (e.type === 'warning') {
            showWarning(e.title, e.message);
          } else {
            showError(e.title, e.message);
          }
        } finally {
          setDownloading(false);
          hideLoading();
        }
      } else {
        await handleLogRequest(targetDevice, params);
      }
    },
    [
      logRequestDialog.device,
      handleLogRequest,
      showLoading,
      hideLoading,
      showSuccess,
      showError,
      showWarning,
      setDownloading,
      t,
      protocol.type,
    ]
  );

  return {
    logRequestDialog,
    openLogRequestDialog,
    closeLogRequestDialog,
    handleLogRequestConfirm,
    isCradleConnected,
    isBleConnected,
    isImportLogEnabled: connectedDevice.type !== 'none',
  };
}
